import type { Permission, ScopeKind, ShareableScopeKind } from "./permissions";

export const BUILTIN_ROLE_IDS = [
  "tenant_admin",
  "space_manager",
  "space_editor",
  "space_viewer",
  "connected_app_editor",
  "connected_app_sharer",
  "connected_app_user",
  "connected_app_viewer",
  "skill_manager",
  "skill_user",
  "network_manager",
  "network_user",
  "network_viewer",
  "user_group_manager",
  "user_group_member",
  "service_account_manager",
  "service_account_user",
  "service_account_viewer",
  "app_creator",
  "skill_creator",
  "space_creator",
  "network_creator",
  "user_group_creator",
  "service_account_creator",
  "connector_sharer",
  "oidc_provider_manager",
  "space_manager_all",
] as const;
export type BuiltinRoleId = (typeof BUILTIN_ROLE_IDS)[number];

export type BuiltinRole = {
  label: string;
  scopeKind: ScopeKind;
  permissions: readonly Permission[];
  userFacing: boolean;
};

export const BUILTIN_ROLES: Record<BuiltinRoleId, BuiltinRole> = {
  tenant_admin: {
    label: "Admin",
    scopeKind: "tenant",
    permissions: ["allow_all"],
    userFacing: false,
  },
  space_manager: {
    label: "Space manager",
    scopeKind: "space",
    permissions: ["space:read", "space:manage"],
    userFacing: true,
  },
  space_editor: {
    label: "Space editor",
    scopeKind: "space",
    permissions: ["space:read", "space:write", "space:push_live"],
    userFacing: true,
  },
  space_viewer: {
    label: "Space viewer",
    scopeKind: "space",
    permissions: ["space:read"],
    userFacing: true,
  },
  connected_app_editor: {
    label: "Connector editor",
    scopeKind: "connected_app",
    permissions: ["connected_app:read", "connected_app:write"],
    userFacing: true,
  },
  connected_app_sharer: {
    label: "Connector sharer",
    scopeKind: "connected_app",
    permissions: ["connected_app:read", "connected_app:share"],
    userFacing: true,
  },
  connected_app_user: {
    label: "Connector user",
    scopeKind: "connected_app",
    permissions: ["connected_app:read", "connected_app:use"],
    userFacing: true,
  },
  connected_app_viewer: {
    label: "Connector viewer",
    scopeKind: "connected_app",
    permissions: ["connected_app:read"],
    userFacing: true,
  },
  skill_manager: {
    label: "Skill manager",
    scopeKind: "skill",
    permissions: ["skill:read", "skill:write"],
    userFacing: true,
  },
  skill_user: {
    label: "Skill user",
    scopeKind: "skill",
    permissions: ["skill:read"],
    userFacing: true,
  },
  network_manager: {
    label: "Network manager",
    scopeKind: "network",
    permissions: ["network:read", "network:write"],
    userFacing: true,
  },
  network_user: {
    label: "Network user",
    scopeKind: "network",
    permissions: ["network:read", "network:use"],
    userFacing: true,
  },
  network_viewer: {
    label: "Network viewer",
    scopeKind: "network",
    permissions: ["network:read"],
    userFacing: true,
  },
  user_group_manager: {
    label: "Group manager",
    scopeKind: "user_group",
    permissions: ["user_group:read", "user_group:write"],
    userFacing: true,
  },
  user_group_member: {
    label: "Group member",
    scopeKind: "user_group",
    permissions: ["user_group:read"],
    userFacing: true,
  },
  service_account_manager: {
    label: "Service account manager",
    scopeKind: "service_account",
    permissions: ["service_account:read", "service_account:manage"],
    userFacing: true,
  },
  service_account_user: {
    label: "Service account user",
    scopeKind: "service_account",
    permissions: ["service_account:read", "service_account:assume"],
    userFacing: true,
  },
  service_account_viewer: {
    label: "Service account viewer",
    scopeKind: "service_account",
    permissions: ["service_account:read"],
    userFacing: true,
  },
  app_creator: {
    label: "Connector creator",
    scopeKind: "tenant",
    permissions: ["tenant:connected_app_create"],
    userFacing: true,
  },
  skill_creator: {
    label: "Skill creator",
    scopeKind: "tenant",
    permissions: ["tenant:skill_create"],
    userFacing: true,
  },
  space_creator: {
    label: "Space creator",
    scopeKind: "tenant",
    permissions: ["tenant:space_create"],
    userFacing: true,
  },
  network_creator: {
    label: "Network creator",
    scopeKind: "tenant",
    permissions: ["tenant:network_create"],
    userFacing: true,
  },
  user_group_creator: {
    label: "Group creator",
    scopeKind: "tenant",
    permissions: ["tenant:user_group_create"],
    userFacing: true,
  },
  service_account_creator: {
    label: "Service account creator",
    scopeKind: "tenant",
    permissions: ["tenant:service_account_create"],
    userFacing: true,
  },
  oidc_provider_manager: {
    label: "OIDC provider manager",
    scopeKind: "oidc_provider",
    permissions: ["oidc_provider:manage"],
    userFacing: false,
  },
  connector_sharer: {
    label: "Connector sharer",
    scopeKind: "tenant",
    permissions: ["tenant:connected_app_share"],
    userFacing: true,
  },
  // Grants read + full management over every space in the tenant. tenant:spaces_read
  // resolves space:read; tenant:spaces_manage resolves space:write/push_live/manage
  // (see TENANT_WRITE_FALLBACK). Because it confers space:manage tenant-wide, a
  // holder can set any space's access list — e.g. grant service accounts
  // editor/viewer — and the grant escalation guard passes since the holder
  // effectively holds those space permissions everywhere.
  // Not userFacing: this is a service-account-provisioning primitive (the Tines
  // bootstrap service account holds it), granted by id via the tenant_roles API
  // rather than picked by a human, so it stays out of the role picker. See
  // rolesForScope.
  space_manager_all: {
    label: "Space manager (all spaces)",
    scopeKind: "tenant",
    permissions: ["tenant:spaces_read", "tenant:spaces_manage"],
    userFacing: false,
  },
};

export type GrantLike = {
  builtinRoleId: string | null;
  customRoleId?: string | null;
  customRole?: {
    rolePermissions?: ReadonlyArray<{ permission: string }>;
  } | null;
};

export function grantPermissions(grant: GrantLike): readonly Permission[] {
  if (grant.builtinRoleId) {
    return (
      BUILTIN_ROLES[grant.builtinRoleId as BuiltinRoleId]?.permissions ?? []
    );
  }
  // A grant with a customRoleId but no customRole/rolePermissions relation means the
  // query forgot to join it — not that the role has zero permissions — and would
  // otherwise silently treat this grant as trivially satisfying any requirement.
  if (
    grant.customRoleId &&
    (grant.customRole === undefined ||
      (grant.customRole && grant.customRole.rolePermissions === undefined))
  ) {
    throw new Error(
      `Grant references custom role ${grant.customRoleId} but its permissions were not loaded — the query needs .related("customRole", (cr) => cr.related("rolePermissions"))`
    );
  }
  return (grant.customRole?.rolePermissions?.map((rp) => rp.permission) ??
    []) as readonly Permission[];
}

export function grantHasPermission(
  grant: GrantLike,
  permission: Permission
): boolean {
  const perms = grantPermissions(grant);
  return perms.includes("allow_all") || perms.includes(permission);
}

// allow_all does NOT match — use grantHasPermission for access checks that should let admins through.
export function grantHasExactPermission(
  grant: GrantLike,
  permission: Permission
): boolean {
  return grantPermissions(grant).includes(permission);
}

export const ALLOW_ALL_ROLE_IDS: readonly BuiltinRoleId[] = (
  Object.entries(BUILTIN_ROLES) as [BuiltinRoleId, BuiltinRole][]
)
  .filter(([, role]) => role.permissions.includes("allow_all"))
  .map(([id]) => id);

export type RoleOption = {
  builtinRoleId?: BuiltinRoleId;
  customRoleId?: string;
  label: string;
};

export type GrantEntry = {
  userGroupId?: string;
  userId?: string;
  serviceAccountId?: string;
  builtinRoleId?: string;
  customRoleId?: string;
};

export function roleLabel(
  grant: GrantEntry,
  customRoles: { id: string; name: string }[]
): string {
  if (grant.builtinRoleId) {
    return (
      BUILTIN_ROLES[grant.builtinRoleId as BuiltinRoleId]?.label ??
      grant.builtinRoleId
    );
  }
  if (grant.customRoleId) {
    return (
      customRoles.find((r) => r.id === grant.customRoleId)?.name ??
      grant.customRoleId
    );
  }
  return "";
}

export function rolesForScope(
  scopeKind: ScopeKind,
  customRoles: { id: string; name: string; scopeKind: string }[]
): RoleOption[] {
  const results: RoleOption[] = [];
  for (const [id, role] of Object.entries(BUILTIN_ROLES) as [
    BuiltinRoleId,
    BuiltinRole,
  ][]) {
    if (role.scopeKind !== scopeKind) continue;
    if (!role.userFacing) continue;
    results.push({ builtinRoleId: id, label: role.label });
  }
  for (const cr of customRoles) {
    if (cr.scopeKind !== scopeKind) continue;
    results.push({ customRoleId: cr.id, label: cr.name });
  }
  return results;
}

export const BUILTIN_ROLE_IDS_GRANTING: Readonly<
  Partial<Record<Permission, readonly BuiltinRoleId[]>>
> = (() => {
  const map: Partial<Record<Permission, BuiltinRoleId[]>> = {};
  for (const [id, role] of Object.entries(BUILTIN_ROLES) as [
    BuiltinRoleId,
    BuiltinRole,
  ][]) {
    for (const p of role.permissions) {
      if (p === "allow_all") continue;
      let bucket = map[p];
      if (!bucket) {
        bucket = [];
        map[p] = bucket;
      }
      bucket.push(id);
    }
  }
  return map;
})();

// Roles the creator of a scope is granted on creation, so they can both
// administer it and use it. Keyed exhaustively by scope kind so a new shareable
// scope can't be added without deciding its creator defaults.
export const DEFAULT_CREATOR_GRANTS: Record<
  ShareableScopeKind,
  readonly BuiltinRoleId[]
> = {
  space: ["space_manager", "space_editor"],
  connected_app: [
    "connected_app_editor",
    "connected_app_sharer",
    "connected_app_user",
  ],
  skill: ["skill_manager", "skill_user"],
  network: ["network_manager", "network_user"],
  service_account: ["service_account_manager", "service_account_user"],
};

export const DEFAULT_PERSONAL_SPACE_GRANTS = DEFAULT_CREATOR_GRANTS.space;

// The permission a principal must hold on a scope to manage its grants — i.e. to
// grant or revoke roles for others. Spaces gate this on space:manage, connectors
// on connected_app:share, service accounts on service_account:manage, and the rest
// on their :write permission.
export const SCOPE_MANAGE_GRANTS_PERMISSION: Record<
  ShareableScopeKind,
  Permission
> = {
  space: "space:manage",
  connected_app: "connected_app:share",
  skill: "skill:write",
  network: "network:write",
  service_account: "service_account:manage",
};

// Expands a scope kind's creator default roles into grants for one principal
// (a user, a group, or a service account), so every creation flow gets the
// creator's roles from a single source of truth. A service account creator must
// be granted as a serviceAccountId: keying the grant on userId would both
// violate the users FK (a service account's audit-attribution userId is its own
// service-account id, not a real user) and grant a nonexistent user, leaving
// the creating service account with no access to what it just created.
export function defaultCreatorGrants(
  scopeKind: ShareableScopeKind,
  principal:
    | { userId: string }
    | { userGroupId: string }
    | { serviceAccountId: string }
): GrantEntry[] {
  return DEFAULT_CREATOR_GRANTS[scopeKind].map((builtinRoleId) => ({
    ...principal,
    builtinRoleId,
  }));
}

// Maps a mutator/request context to the principal its creator grants belong to:
// the acting service account when the caller authenticates as one, otherwise
// the human user. ctx.userId is audit attribution only and is unsafe as a grant
// target for service accounts (see defaultCreatorGrants).
export function creatorGrantPrincipal(ctx: {
  userId: string;
  principal?: { kind: string; serviceAccountId?: string };
}): { userId: string } | { serviceAccountId: string } {
  if (
    ctx.principal?.kind === "serviceAccount" &&
    ctx.principal.serviceAccountId
  ) {
    return { serviceAccountId: ctx.principal.serviceAccountId };
  }
  return { userId: ctx.userId };
}

// The human to attribute a `created_by_user_id` audit column to. A pure service
// account credential has no owning human — its ctx.userId is its own
// service-account id, which is not a valid users FK — so creation is attributed
// to nobody (null). A service account acting for a consenting human keeps that
// human's id.
export function creatorUserId(ctx: {
  userId: string;
  principal?: { kind: string; serviceAccountId?: string };
}): string | null {
  if (
    ctx.principal?.kind === "serviceAccount" &&
    ctx.userId === ctx.principal.serviceAccountId
  ) {
    return null;
  }
  return ctx.userId;
}
